import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { notify } from "../components/Toast";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../api/client";
import ProjectsTable from "../components/ProjectsTable";
import ProjectTemplatesModal from "../components/ProjectTemplatesModal";
import { useAuth } from "../hooks/useAuth";
import { useOrgCatalog } from "../hooks/useOrgCatalog";
import { OneOffTasksWidget, OneTimeEventsWidget, SidebarProjectList, } from "./projects/Sidebar";
import { NewProjectForm } from "./projects/NewProjectForm";
import { ProjectDetail } from "./projects/ProjectDetail";
export default function ProjectsPage() {
    const { user } = useAuth();
    // Owners manage projects + tasks like managers (backend scopes both to
    // their BUs via has_bu_power). Without owner here the project-create
    // form hid the "Project" type and all management controls, so an owner
    // looked like a worker — the reported Test Test issue.
    const canManage = user?.role === "admin" ||
        user?.role === "manager" ||
        user?.role === "owner";
    const [searchParams, setSearchParams] = useSearchParams();
    const [projects, setProjects] = useState([]);
    // Bumped after create / delete so the ProjectsTable (which fetches its
    // own feed independently from /users/me/project-table) re-runs its
    // loader. Without this the sidebar updated optimistically but the
    // table view still rendered stale data until manual refresh / focus.
    const [projectsVersion, setProjectsVersion] = useState(0);
    // Mirror of the table view's active "Show tasks" filter. When it's "due7"
    // the table itself renders the due-soon one-off tasks + meetings, so we
    // hide the standalone one-off / one-time widgets below it to avoid showing
    // a filtered list and a full list side by side.
    const [tableFilter, setTableFilter] = useState("all");
    // Seed the table's "Show tasks" filter when deep-linked (e.g. Today's
    // "View all in My projects" → ?view=table&taskfilter=mine). undefined =
    // no override, the table keeps its own default.
    const [initialTableFilter, setInitialTableFilter] = useState(undefined);
    const [activeId, setActiveId] = useState(null);
    const [tree, setTree] = useState(null);
    const [members, setMembers] = useState([]);
    const { businessUnits: units, activities } = useOrgCatalog();
    const [showNew, setShowNew] = useState(false);
    // Templates manager — separate from "New project" because saved
    // templates are a longer-lived, shared concept and the modal needs more
    // room (template list + task preview + instantiate form). Opens from
    // the sidebar "Templates" button next to "+ New".
    const [showTemplates, setShowTemplates] = useState(false);
    // Detail-pane visibility. When false, the project detail on the right
    // is hidden and the sidebar takes the full page width with richer
    // rows. Persisted across sessions so the user lands back in their
    // preferred mode. Default = true (show detail) when a project is
    // selected; we auto-collapse to "list-only" when nothing is selected
    // so the empty right-pane isn't wasted space.
    const [detailCollapsed, setDetailCollapsed] = useState(() => {
        try {
            return localStorage.getItem("pm-detail-collapsed") === "1";
        }
        catch {
            return false;
        }
    });
    // Page-level view mode: sidebar (the classic project list + detail
    // pane) or table (the flat hierarchical table). Persisted across
    // sessions so users stick with whichever mode they prefer.
    const [viewMode, setViewMode] = useState(() => {
        try {
            const v = localStorage.getItem("pm-view-mode");
            if (v === "sidebar" || v === "table")
                return v;
        }
        catch {
            /* fall through */
        }
        return "sidebar";
    });
    function changeViewMode(v) {
        setViewMode(v);
        try {
            localStorage.setItem("pm-view-mode", v);
        }
        catch {
            /* ignore quota */
        }
    }
    function setCollapsed(v) {
        setDetailCollapsed(v);
        try {
            localStorage.setItem("pm-detail-collapsed", v ? "1" : "0");
        }
        catch {
            /* ignore */
        }
    }
    // Pending handoff queue. Backend scopes the rows server-side:
    //   admin/manager (in scope) → all pending handoffs they can review
    //   worker                   → their own sent + received pending requests
    // So the same fetch covers both audiences; the UI just hides Approve/Reject
    // for non-reviewers.
    const [handoffs, setHandoffs] = useState([]);
    // ?project=ID query param — populated when the user clicks "Open project ↗"
    // in the scheduler side panel. We adopt it as the initial selection if the
    // project exists in the loaded list, then clear the param so a manual click
    // on a different project doesn't fight with the URL.
    const requestedProjectId = searchParams.get("project");
    async function loadProjects() {
        const ps = await api.get("/projects");
        setProjects(ps);
        // Only auto-open a project when one was explicitly deep-linked via
        // ?project=... (e.g. from the scheduler / calendar). Otherwise leave
        // the detail pane closed so the page opens on the full sidebar overview
        // instead of forcing the first project (the alphabetically/priority-first
        // one) open every time.
        if (!activeId && requestedProjectId && ps.length) {
            const hit = ps.find((p) => p.id === requestedProjectId);
            if (hit)
                setActiveId(hit.id);
        }
        // Deep-link from the scheduler / calendar via ?project=... means
        // "open this project's detail". Force the detail pane open even if
        // the user previously collapsed it — they explicitly asked to see
        // the project, so show it.
        if (requestedProjectId) {
            setCollapsed(false);
            searchParams.delete("project");
            setSearchParams(searchParams, { replace: true });
        }
    }
    async function loadTree(id) {
        const t = await api.get(`/projects/${id}`);
        setTree(t);
    }
    async function loadMembers() {
        if (!canManage)
            return;
        // GET /users is admin-only. Managers and owners must instead use the
        // BU-scoped /users/coworkers roster (active users who share one of their
        // business units, plus admins) — otherwise the call 403s, `members`
        // stays empty, and every assignee picker shows "No teammates available."
        // include_self=true so the caller can assign work to themselves and see
        // their own name on assignee pills.
        const path = user?.role === "admin"
            ? "/users"
            : "/users/coworkers?include_self=true";
        const u = await api.get(path);
        setMembers(u);
    }
    async function loadHandoffs() {
        // Fail soft — handoff routes only exist post-migration-0015, so if a
        // freshly redeployed backend hasn't run the migration yet we just keep
        // the queue empty rather than blowing up the whole P&M page.
        try {
            const rows = await api.get("/tasks/handoffs");
            setHandoffs(rows);
        }
        catch {
            setHandoffs([]);
        }
    }
    async function reviewHandoff(id, action) {
        try {
            await api.patch(`/tasks/handoffs/${id}`, { action });
            await loadHandoffs();
            // Approved handoffs swap the task's assignee — if the user is currently
            // viewing the affected project, refresh the tree so the new assignee
            // shows up immediately.
            if (activeId)
                loadTree(activeId);
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't update handoff");
        }
    }
    useEffect(() => {
        loadProjects();
        loadMembers();
        loadHandoffs();
    }, []);
    // Deep-link from elsewhere (Today's "View all in My projects") can request
    // the table view pre-filtered to "Mine". Apply once on mount, then strip
    // the params so a manual toggle later doesn't fight the URL.
    useEffect(() => {
        const view = searchParams.get("view");
        const tf = searchParams.get("taskfilter");
        if (view === "table")
            changeViewMode("table");
        if (tf)
            setInitialTableFilter(tf);
        if (view || tf) {
            searchParams.delete("view");
            searchParams.delete("taskfilter");
            setSearchParams(searchParams, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (activeId)
            loadTree(activeId);
    }, [activeId]);
    async function createProject(name, type, due_date, business_unit_id, activity_id, priority, 
    // Optional recurrence bundle — only relevant when type === "recurring_event".
    // When present, we create the project AND a default meeting task with these
    // recurrence fields in one user action so the admin doesn't have to drill in.
    recurrence, 
    // One-off task / one-time event invitees + (for events) the time the
    // meeting happens. Creator is NOT auto-added — the form has its own
    // "Add me to it" toggle that appends the caller's id to this list.
    extras) {
        // Recurring events don't have a one-off due_date — null it out even if
        // the caller passed something (defensive; the form already hides the field).
        const dueForBody = type === "recurring_event" ? null : due_date;
        const p = await api.post("/projects", {
            name,
            type,
            due_date: dueForBody,
            priority,
            business_unit_id,
            activity_id,
            // Forwarded so the backend can auto-create the task + assignments +
            // event TimeBlocks in one round-trip for one_off_task and
            // one_time_event projects.
            assignee_ids: extras?.assignee_ids ?? [],
            event_at: extras?.event_at ?? null,
            event_duration_minutes: extras?.event_duration_minutes ?? null,
        });
        // For recurring meetings, spin up a default task with the admin-provided
        // recurrence so the project isn't an empty shell. Admins can rename the
        // task and add assignees afterward, then push to assignees from the
        // Projects & Meetings detail.
        if (type === "recurring_event" && recurrence) {
            await api.post("/tasks", {
                project_id: p.id,
                title: name, // default task title = project name; admin can rename
                recurrence_frequency: recurrence.frequency,
                recurrence_days_of_week: recurrence.frequency === "weekly" ? recurrence.days_of_week : null,
                recurrence_day_of_month: recurrence.frequency === "monthly" ? recurrence.day_of_month : null,
                recurrence_time_of_day: `${recurrence.time_of_day}:00`,
                recurrence_duration_minutes: recurrence.duration_minutes,
                // Lock the schedule to Eastern. The materializer in schedule-for-
                // assignees forces this regardless, but we set it here too so the
                // task's stored value is consistent.
                recurrence_timezone: "America/New_York",
            });
        }
        setProjects((cur) => [...cur, p]);
        // Bump the table view's refresh signal so it refetches the
        // /users/me/project-table feed and surfaces the new row without
        // requiring a manual refresh or a window-focus event.
        setProjectsVersion((v) => v + 1);
        setActiveId(p.id);
        setShowNew(false);
    }
    async function deleteProject(projectId, projectName) {
        if (!confirm(`Delete "${projectName}"? This permanently removes the project, all its tasks, and all checklist items. ` +
            "Time blocks already linked to those items keep their schedule but lose the link. This cannot be undone.")) {
            return;
        }
        try {
            await api.del(`/projects/${projectId}`);
            // Remove from sidebar list and clear selection if it was active.
            setProjects((cur) => cur.filter((p) => p.id !== projectId));
            // Same refresh signal as create — keeps the table view in sync
            // without a manual reload.
            setProjectsVersion((v) => v + 1);
            if (activeId === projectId) {
                setActiveId(null);
                setTree(null);
            }
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Delete failed");
        }
    }
    // List-only when the user explicitly collapses the detail pane OR
    // there's no project selected (nothing meaningful to show on the
    // right) OR the table view is active (it spans the page width).
    // Drives the page layout + sidebar expanded mode together.
    const listOnly = detailCollapsed || !tree || viewMode === "table";
    return (_jsxs("div", { className: `page projects-page ${listOnly ? "projects-page-list-only" : ""}`, children: [_jsxs("aside", { className: `sidebar ${viewMode === "table" ? "sidebar-table" : ""}`, children: [_jsxs("div", { className: "sidebar-head", children: [_jsx("h2", { children: "Projects & Meetings" }), _jsxs("div", { className: "sidebar-head-actions", children: [_jsxs("div", { className: "pm-view-toggle", role: "tablist", "aria-label": "View mode", children: [_jsx("button", { type: "button", role: "tab", "aria-selected": viewMode === "sidebar", className: `pm-view-toggle-btn ${viewMode === "sidebar" ? "active" : ""}`, onClick: () => changeViewMode("sidebar"), title: "Sidebar view: project list + detail pane", children: "Sidebar" }), _jsx("button", { type: "button", role: "tab", "aria-selected": viewMode === "table", className: `pm-view-toggle-btn ${viewMode === "table" ? "active" : ""}`, onClick: () => changeViewMode("table"), title: "Table view: every project + tasks in one flat list", children: "Table" })] }), canManage && (_jsxs("button", { onClick: () => setShowTemplates(true), className: "pm-head-templates", type: "button", title: "Reusable project blueprints", "aria-label": "Open templates", children: [_jsx("span", { "aria-hidden": "true", children: "\u2398" }), " Templates"] })), _jsxs("button", { onClick: () => setShowNew(true), className: "pm-head-new", type: "button", children: [_jsx("span", { "aria-hidden": "true", children: "+" }), " New"] })] })] }), showNew && (_jsx(NewProjectForm, { onCreate: createProject, onCancel: () => setShowNew(false), units: units, activities: activities, defaultUnitId: user?.business_unit_id ?? null, canManage: canManage, members: members, currentUserId: user?.id ?? null, 
                        // Admins can file a project under any BU; everyone else is
                        // scoped to the units they belong to (null = all). Mirrors
                        // the backend create_project guard so the dropdown can't
                        // offer a BU the caller would be 403'd for.
                        allowedUnitIds: user?.role === "admin" || user?.role === "owner"
                            ? null
                            : user?.business_unit_ids ?? [] })), viewMode === "table" ? (_jsxs(_Fragment, { children: [_jsx(ProjectsTable, { refreshSignal: projectsVersion, initialFilter: initialTableFilter, onFilterChange: setTableFilter, onSelectProject: (id) => {
                                    // Clicking a project name in the table opens its detail —
                                    // hop back to sidebar mode so the user can edit it in the
                                    // familiar two-pane layout. The toggle is still there to
                                    // bounce back to the table.
                                    setActiveId(id);
                                    changeViewMode("sidebar");
                                    setCollapsed(false);
                                } }), tableFilter !== "due7" && (_jsxs(_Fragment, { children: [_jsx(OneOffTasksWidget, { activeId: activeId, onSelect: (id) => {
                                            setActiveId(id);
                                            changeViewMode("sidebar");
                                            setCollapsed(false);
                                        }, refreshSignal: projectsVersion }), _jsx(OneTimeEventsWidget, { activeId: activeId, onSelect: (id) => {
                                            setActiveId(id);
                                            changeViewMode("sidebar");
                                            setCollapsed(false);
                                        }, canManage: canManage, refreshSignal: projectsVersion, onChanged: () => setProjectsVersion((v) => v + 1) })] }))] })) : (_jsx(SidebarProjectList, { projects: projects, units: units, activeId: activeId, onSelect: (id) => {
                            // Selecting a project auto-restores the detail pane — if the
                            // user collapsed it earlier, clicking a project clearly means
                            // "open me", so flip back to the two-pane layout.
                            setActiveId(id);
                            if (detailCollapsed)
                                setCollapsed(false);
                        }, canManage: canManage, currentUserId: user?.id ?? null, expanded: listOnly })), _jsx(PendingHandoffsPanel, { handoffs: handoffs, currentUserId: user?.id ?? null, canReview: canManage, onReview: reviewHandoff, onRefresh: loadHandoffs })] }), !listOnly && (_jsxs("section", { className: "project-detail", children: [_jsx("div", { className: "project-detail-toolbar", children: _jsx("button", { type: "button", className: "btn-link project-detail-hide", onClick: () => setCollapsed(true), title: "Hide this pane and expand the project list", children: "\u2190 Hide" }) }), tree && (_jsx(ProjectDetail, { tree: tree, members: members, units: units, activities: activities, canManage: canManage, currentUserId: user?.id ?? null, onChanged: () => {
                            // Reload BOTH the detail tree and the sidebar list so edits
                            // made in the detail pane (priority, name, due date, BU,
                            // status) take effect everywhere immediately — previously
                            // only the tree refreshed, so a priority change didn't move
                            // / relabel the project in the sidebar until a full reload.
                            if (activeId)
                                loadTree(activeId);
                            loadProjects();
                        }, onDelete: () => deleteProject(tree.id, tree.name) }))] })), showTemplates && (_jsx(ProjectTemplatesModal, { canManage: canManage, currentUserId: user?.id ?? null, isAdmin: user?.role === "admin", units: units, onClose: () => setShowTemplates(false), onInstantiated: async (newProjectId) => {
                    // Reload the project list so the freshly-instantiated project
                    // appears in the sidebar, then select it. loadProjects() only
                    // auto-selects when activeId is null, so set it explicitly
                    // after the reload to guarantee navigation.
                    await loadProjects();
                    setActiveId(newProjectId);
                } }))] }));
}
function PendingHandoffsPanel({ handoffs, currentUserId, canReview, onReview, onRefresh, }) {
    const [busyId, setBusyId] = useState(null);
    // We display "pending" only — approved/rejected handoffs disappear from
    // the queue once they've been reviewed (the underlying GET endpoint
    // already filters to pending server-side for both audiences, but we
    // belt-and-suspender it here in case that changes).
    const pending = handoffs.filter((h) => h.status === "pending");
    if (pending.length === 0) {
        // Don't render anything when there's nothing to act on — the sidebar
        // stays uncluttered for the common case.
        return null;
    }
    async function act(id, action) {
        setBusyId(id);
        try {
            await onReview(id, action);
        }
        finally {
            setBusyId(null);
        }
    }
    function whenLabel(iso) {
        const d = new Date(iso);
        const now = new Date();
        const ms = now.getTime() - d.getTime();
        const mins = Math.floor(ms / 60000);
        if (mins < 1)
            return "just now";
        if (mins < 60)
            return `${mins}m ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24)
            return `${hrs}h ago`;
        const days = Math.floor(hrs / 24);
        if (days < 7)
            return `${days}d ago`;
        return d.toLocaleDateString();
    }
    return (_jsxs("div", { className: "sidebar-section pending-handoffs", children: [_jsxs("div", { className: "sidebar-section-head", children: [_jsxs("h3", { children: ["Pending handoffs", " ", _jsxs("span", { className: "muted", style: { fontWeight: 400 }, children: ["\u00B7 ", pending.length] })] }), _jsx("button", { type: "button", className: "btn-link", onClick: () => void onRefresh(), title: "Refresh", children: "\u21BB" })] }), !canReview && (_jsx("p", { className: "muted small", style: { marginTop: "-0.25rem" }, children: "Your manager or an admin needs to approve these before the task actually changes hands." })), _jsx("ul", { className: "handoff-list", children: pending.map((h) => {
                    const isFrom = currentUserId && h.from_user_id === currentUserId;
                    const isTo = currentUserId && h.to_user_id === currentUserId;
                    // Side label for workers — admins/managers don't need this since
                    // they're reviewing on behalf of others.
                    let sideLabel = null;
                    if (!canReview) {
                        if (isFrom)
                            sideLabel = "You → " + h.to_user_name;
                        else if (isTo)
                            sideLabel = h.from_user_name + " → You";
                        else
                            sideLabel = h.from_user_name + " → " + h.to_user_name;
                    }
                    return (_jsxs("li", { className: "handoff-row", children: [_jsx("div", { className: "handoff-title", children: h.task_title }), _jsx("div", { className: "muted small", children: canReview ? (_jsxs(_Fragment, { children: [_jsx("strong", { children: h.from_user_name }), " \u2192 ", _jsx("strong", { children: h.to_user_name }), " · ", whenLabel(h.requested_at)] })) : (_jsxs(_Fragment, { children: [sideLabel, " \u00B7 ", whenLabel(h.requested_at)] })) }), h.note && (_jsxs("p", { className: "muted small", style: { marginTop: "0.25rem", whiteSpace: "pre-wrap" }, children: ["\u201C", h.note, "\u201D"] })), canReview ? (_jsxs("div", { className: "handoff-actions", children: [_jsx("button", { type: "button", className: "btn-primary", disabled: busyId === h.id, onClick: () => act(h.id, "approve"), title: "Reassign the task to the requested coworker", children: busyId === h.id ? "Approving…" : "Approve" }), _jsx("button", { type: "button", className: "btn-danger", disabled: busyId === h.id, onClick: () => act(h.id, "reject"), children: "Reject" })] })) : (_jsx("div", { className: "muted small", style: { marginTop: "0.25rem" }, children: "Awaiting manager / admin review" }))] }, h.id));
                }) })] }));
}
